"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface EyeIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface EyeIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const EyeIcon = forwardRef<EyeIconHandle, EyeIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const eyeControls = useAnimation();
    const pupilControls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () => {
          if (reduced) {
            eyeControls.start("open");
            pupilControls.start("center");
          } else {
            eyeControls.start("blink");
            pupilControls.start("scan");
          }
        },
        stopAnimation: () => {
          eyeControls.start("open");
          pupilControls.start("center");
        },
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        eyeControls.start("blink");
        pupilControls.start("scan");
      } else if (!isHovered) {
        eyeControls.start("open");
        pupilControls.start("center");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) {
        eyeControls.start("blink");
        pupilControls.start("scan");
      }
    }, [eyeControls, pupilControls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) {
        eyeControls.start("open");
        pupilControls.start("center");
      }
    }, [eyeControls, pupilControls]);

    const eyeVariants: Variants = {
      open: { scaleY: 1 },
      blink: {
        scaleY: [1, 0.1, 1],
        transition: {
          duration: 0.25 * duration,
          repeatDelay: 2.4,
          ease: "easeInOut",
        },
      },
    };

    const pupilVariants: Variants = {
      center: { x: 0 },
      scan: {
        x: [-2, 2, -1, 1, 0],
        transition: {
          duration: 1.6 * duration,
          ease: "easeInOut",
        },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.path
          d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"
          animate={eyeControls}
          initial="open"
          variants={eyeVariants}
          style={{
            transformBox: "fill-box",
            transformOrigin: "center",
          }}
        />
        <motion.circle
          cx="12"
          cy="12"
          r="3"
          animate={pupilControls}
          initial="center"
          variants={pupilVariants}
        />
      </motion.svg>
    );
  },
);

EyeIcon.displayName = "EyeIcon";
export { EyeIcon };
